import { Link } from "react-router-dom";
import { Sparkles, Volume2, Download, ArrowRight } from "lucide-react";

const MERCH = [
  { slug: "whoracle-mascot", title: "Whoracle · Full Body", note: "the mascot in her full glory" },
  { slug: "oracle-tarot", title: "Oracle Tarot", note: "the card that started it all" },
  { slug: "third-eye", title: "Third Eye", note: "for the intuitive ones" },
  { slug: "commandments-tablet", title: "Commandments Tablet", note: "written in matrix stone" },
];

export const Whoracle = () => {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="whoracle-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28 relative grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-4">
              <Sparkles className="w-3 h-3" />
              // sub-brand · sAimonSays.Ai
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              meet<br />
              <span className="text-[color:var(--accent-matrix)]">Whoracle</span>
              <span className="cursor" />
            </h1>
            <p className="mt-8 max-w-xl text-neutral-400 text-base sm:text-lg leading-relaxed">
              hooded. curvy. cocky. she reads your future by reading your prompts.
              part oracle, part hacker, all attitude. she is the voice that narrates the site
              when you tap <span className="text-[color:var(--accent-amber)]">Read Aloud</span>.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/refinery"
                data-testid="whoracle-cta-refinery"
                className="inline-flex items-center gap-2 bg-[color:var(--accent-matrix)] text-black px-5 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white duration-150"
                style={{ transitionProperty: "background-color" }}
              >
                <Sparkles className="w-4 h-4" />
                ask whoracle for a prompt <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                type="button"
                data-testid="whoracle-cta-read-aloud"
                onClick={() => {
                  // Trigger the global Read Aloud toggle. Users can then hear Whoracle.
                  const btn = document.querySelector('[data-testid="read-aloud-toggle"]');
                  if (btn) btn.click();
                }}
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs uppercase tracking-widest hover:border-[color:var(--accent-matrix)] hover:text-[color:var(--accent-matrix)] duration-150"
                style={{ transitionProperty: "color, border-color" }}
              >
                <Volume2 className="w-4 h-4" /> hear her voice
              </button>
            </div>
          </div>
          <div className="relative group order-first lg:order-last mx-auto lg:mx-0" data-testid="whoracle-hero-image">
            <div className="whoracle-glow absolute -inset-10 bg-[color:var(--accent-matrix)]/20 blur-3xl pointer-events-none" />
            <img
              src="/social/generated/whoracle-logo-v4.png"
              alt="Whoracle"
              className="whoracle-float relative w-80 sm:w-[26rem] lg:w-[34rem] h-auto object-contain drop-shadow-[0_0_50px_rgba(0,255,120,0.35)]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Lore */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-4">// origin</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-8 tracking-tight">the story so far</h2>
          <div className="space-y-6 text-neutral-300 text-base leading-relaxed">
            <p>
              Whoracle was drafted after too many nights of watching AI hallucinate confidently at people
              who did not deserve it. She is what happens when an oracle joins the resistance:
              hoodie up, crystal ball on, receipts ready.
            </p>
            <p>
              She narrates every page you visit. She lives in the top-left corner of the site.
              She is <span className="text-[color:var(--accent-amber)]">not</span> a chatbot. She is a stance —
              that AI should be truthful, useful, and slightly amused at your predicament.
            </p>
            <p className="text-neutral-500 italic">
              "I already know what you were going to ask. Sit closer."
            </p>
          </div>
        </div>
      </section>

      {/* Attributes */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-8">// spec sheet</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { k: "voice", v: "shimmer · sultry, unbothered" },
              { k: "palette", v: "matrix green + black" },
              { k: "vibe", v: "75% sexy · 15% mystical · 10% nerdy · 100% intelligent" },
              { k: "gesture", v: "hang-loose (shaka)" },
              { k: "prop", v: "green crystal ball" },
              { k: "hood", v: "always up" },
              { k: "affiliation", v: "sAimonSays.Ai" },
              { k: "mood", v: "already knew that" },
            ].map((row) => (
              <div key={row.k} className="border border-white/10 p-4 hover:border-[color:var(--accent-matrix)] duration-150"
                   style={{ transitionProperty: "border-color" }}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">{row.k}</div>
                <div className="text-sm text-neutral-200 leading-snug">{row.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Merch strip */}
      <section>
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// merch</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">wear her, hang her, gift her</h2>
              <p className="mt-3 text-neutral-400 text-sm max-w-xl">
                print-ready hi-res files, solid + transparent backgrounds, 300 DPI. drop them into
                Printful, Printify, Redbubble — or use them however you want.
              </p>
            </div>
            <Link
              to="/print-ready"
              data-testid="whoracle-print-ready-cta"
              className="inline-flex items-center gap-2 border border-[color:var(--accent-amber)] text-[color:var(--accent-amber)] hover:bg-[color:var(--accent-amber)] hover:text-black px-4 py-2 text-xs uppercase tracking-widest duration-150"
              style={{ transitionProperty: "background-color, color" }}
            >
              <Download className="w-3 h-3" /> all print files
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {MERCH.map((m) => (
              <Link
                key={m.slug}
                to="/print-ready"
                data-testid={`whoracle-merch-${m.slug}`}
                className="group border border-white/10 hover:border-[color:var(--accent-matrix)] p-3 duration-150"
                style={{ transitionProperty: "border-color" }}
              >
                <div
                  className="aspect-square mb-3 overflow-hidden"
                  style={{
                    backgroundImage:
                      "linear-gradient(45deg, #111 25%, transparent 25%), linear-gradient(-45deg, #111 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #111 75%), linear-gradient(-45deg, transparent 75%, #111 75%)",
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0",
                    backgroundColor: "#1a1a1a",
                  }}
                >
                  <img
                    src={`/print-ready/${m.slug}-transparent-300dpi.png`}
                    alt={m.title}
                    className="w-full h-full object-contain group-hover:scale-105 duration-500"
                    style={{ transitionProperty: "transform" }}
                    loading="lazy"
                  />
                </div>
                <div className="text-xs font-bold text-white leading-snug">{m.title}</div>
                <div className="text-[10px] text-neutral-500 mt-1">{m.note}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Whoracle;
